<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import type { FieldSchema } from '../../../types';
import { useAToast } from '@Atoms';

// One image, uploaded the moment it is picked; the model is the stored image's URL ('' when none).
// The caller passes `field.settings.uploadImage(file) => Promise<url>` (the contract AFieldEditor
// already uses), so this field never knows an endpoint. FILE is different: it keeps File objects
// for a later form submit, which a form that saves on every change cannot use.

const props = withDefaults(
  defineProps<{
    accept?: string;
    disabled?: boolean;
    error?: string | boolean;
    field?: FieldSchema;
  }>(),
  {
    accept: 'image/jpeg,image/png,image/webp,image/gif',
  }
);

const { notify, TOAST_TYPES } = useAToast();

const TOAST_UPLOAD_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'components.fileUploader.uploadImageError',
  type: TOAST_TYPES.ERROR,
};

const fileInput = useTemplateRef<HTMLInputElement>('fileInput');
const model = defineModel<string>();

const uploading = ref(false);

const isDisabled = computed(() => !!props.field?.state?.disabled || props.disabled || uploading.value);

const uploadButtonLabel = computed(() => {
  if (uploading.value) return 'components.fileUploader.uploadingImage';

  return model.value ? 'components.fileUploader.replaceImage' : 'components.fileUploader.uploadImage';
});

// EVENTS
function onChooseFile() {
  fileInput.value?.click();
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  const uploadImage = props.field?.settings?.uploadImage;
  const hasNothingToUpload = !file || !uploadImage;
  if (hasNothingToUpload) return;

  uploading.value = true;

  uploadImage(file)
    .then((url) => (model.value = url))
    .catch(() => notify(TOAST_UPLOAD_ERROR))
    .finally(() => onUploadSettled(input));
}

function onUploadSettled(input: HTMLInputElement) {
  uploading.value = false;
  input.value = '';
}

function onRemove() {
  model.value = '';
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <img
      v-if="model"
      class="max-h-40 w-full rounded border border-slate-200 object-cover"
      :alt="field?.label"
      :src="model"
    />

    <div class="flex flex-wrap items-center gap-2">
      <Button
        outlined
        icon="pi pi-upload"
        severity="secondary"
        size="small"
        type="button"
        :class="{ 'border-red-500 text-red-500': !!error }"
        :disabled="isDisabled"
        :label="$t(uploadButtonLabel)"
        :loading="uploading"
        @click="onChooseFile"
      />

      <Button
        v-if="model"
        text
        icon="pi pi-times"
        severity="secondary"
        size="small"
        type="button"
        :disabled="isDisabled"
        :label="$t('common.remove')"
        @click="onRemove"
      />

      <input
        class="hidden"
        ref="fileInput"
        type="file"
        :accept
        @change="onFileSelected"
      />
    </div>
  </div>
</template>
